// src/components/AdvertSlotCard.jsx
import { Box, Flex, Text, Image } from "@chakra-ui/react";
import { useSeason } from "../context/SeasonContext";

const MAX_OFFER_TITLE_LENGTH = 19;

function shortenOfferTitle(value) {
  const title = String(value || "Local coupon offer").trim();

  if (title.length <= MAX_OFFER_TITLE_LENGTH) {
    return title;
  }

  return `${title.slice(0, MAX_OFFER_TITLE_LENGTH - 1).trimEnd()}…`;
}

export default function AdvertSlotCard({
  slot,
  variant = "public",
  authBusiness = null,
  authLoading = false,
}) {
  const { seasonLabel } = useSeason();

  if (!slot) return null;

  const token = slot?.token || slot?.redeem_token || null;

  const {
    business_name,
    qr_code_url,
    offer_title,
    status,
    category_name,
    category_color,
    category_icon,
    business_logo_url,
    logo_url,
    flyer_id,
    slot_number,
  } = slot;

  const normalizedStatus = String(status || "").toLowerCase().trim();
  const isBranding = normalizedStatus === "branding" || variant === "branding";
  const isBooked = normalizedStatus === "booked";
  const isBusinessUser = Boolean(authBusiness);
  const isLoadingAuth = Boolean(authLoading);
  const businessLogo = business_logo_url || logo_url || null;
  const displayOfferTitle = shortenOfferTitle(offer_title);

  const Scissors = () => (
    <Text
      fontSize="xs"
      color="gray.500"
      position="absolute"
      top="-6px"
      right="-6px"
      pointerEvents="none"
      zIndex={10}
    >
      ✂️
    </Text>
  );

  // BRANDING SLOT
  if (isBranding) {
    return (
      <Box
        position="relative"
        border="2px dashed #e11d48"
        bg="white"
        rounded="md"
        p={2}
        height="170px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
      >
        <Scissors />

        <Text fontSize="1rem" fontWeight="extrabold" noOfLines={1}>
          Postcode.Coupons
        </Text>

        <Image
          src="/logo1024.png"
          alt="Postcode.Coupons logo"
          height="48px"
          width="48px"
          my={1}
        />

        <Text
          fontSize="0.9rem"
          fontWeight="extrabold"
          color="red.700"
          letterSpacing="1px"
        >
          SCAN. SAVE. KEEP.
        </Text>

        <Text
          fontSize="0.55rem"
          fontWeight="bold"
          mt={1}
          opacity={0.9}
          noOfLines={1}
        >
          {seasonLabel}
        </Text>
      </Box>
    );
  }

  // AVAILABLE / UNSOLD SLOT
  if (!isBooked) {
    const handleAvailableSlotClick = () => {
      if (isLoadingAuth) return;

      if (!isBusinessUser) {
        window.location.href = "/business/signup";
        return;
      }

      if (!flyer_id || !slot_number) {
        console.error("[AdvertSlotCard] Missing flyer_id or slot_number", {
          flyer_id,
          slot_number,
        });
        return;
      }

      window.location.href = `/business/slot/${flyer_id}/${slot_number}/book`;
    };

    return (
      <Box
        position="relative"
        border="2px dashed #f56565"
        bg="red.50"
        rounded="md"
        p={2}
        height="170px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        cursor={isLoadingAuth ? "wait" : "pointer"}
        opacity={isLoadingAuth ? 0.65 : 1}
        transition="all 0.15s ease"
        role="button"
        tabIndex={isLoadingAuth ? -1 : 0}
        aria-disabled={isLoadingAuth}
        aria-label={
          isLoadingAuth
            ? "Checking business account"
            : isBusinessUser
              ? `Book coupon slot ${slot_number}`
              : "Offer your coupon in this slot"
        }
        _hover={
          isLoadingAuth
            ? {}
            : {
                borderColor: "red.500",
                bg: "red.100",
                transform: "translateY(-2px)",
                boxShadow: "md",
              }
        }
        _focusVisible={{
          borderColor: "red.500",
          boxShadow: "0 0 0 3px rgba(229, 62, 62, 0.25)",
          outline: "none",
        }}
        onClick={handleAvailableSlotClick}
        onKeyDown={(event) => {
          if (!isLoadingAuth && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            handleAvailableSlotClick();
          }
        }}
      >
        <Scissors />

        <Image
          src="/postcode.couponsQR.png"
          alt="Postcode.Coupons"
          height="42px"
          width="42px"
          mb={1}
        />

        <Text
          fontSize="sm"
          fontWeight="extrabold"
          color="red.700"
          textAlign="center"
          noOfLines={2}
        >
          {isLoadingAuth ? "Loading…" : "🏷️ YOUR COUPON HERE"}
        </Text>

        {!isLoadingAuth && (
          <Text
            fontSize="0.65rem"
            color="gray.600"
            mt={1}
            textAlign="center"
            lineHeight="1.2"
          >
            Reach local households
          </Text>
        )}

        <Box
          mt={2}
          px={3}
          py={1}
          bg={isLoadingAuth ? "gray.400" : "red.500"}
          color="white"
          rounded="full"
          fontSize="0.68rem"
          fontWeight="extrabold"
          lineHeight="1.2"
          boxShadow={isLoadingAuth ? "none" : "sm"}
        >
          {isLoadingAuth
            ? "PLEASE WAIT…"
            : isBusinessUser
              ? "BOOK NOW"
              : "OFFER YOUR COUPON"}
        </Box>

        <Text fontSize="0.6rem" color="gray.500" mt={1} noOfLines={1}>
          {seasonLabel}
        </Text>
      </Box>
    );
  }

  // BOOKED SLOT
  const redeemUrl = token
    ? `https://postcode.coupons/r/${encodeURIComponent(token)}`
    : null;

  return (
    <Box
      position="relative"
      border="2px dashed #e11d48"
      bg="white"
      rounded="md"
      height="170px"
      p={2}
      display="grid"
      gridTemplateRows="24px 18px 18px 52px 14px 10px"
      rowGap="2px"
      overflow="hidden"
    >
      <Scissors />

      <Flex
        bg="red.600"
        color="white"
        align="center"
        px={2}
        height="24px"
        rounded="md"
        overflow="hidden"
      >
        <Text
          width="100%"
          fontSize="0.65rem"
          fontWeight="bold"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {business_name || "Local Business"}
        </Text>
      </Flex>

      {category_name ? (
        <Flex
          bg={category_color || "red.500"}
          color="white"
          px={2}
          width="100%"
          height="18px"
          rounded="md"
          align="center"
          overflow="hidden"
        >
          <Text
            width="100%"
            fontSize="0.55rem"
            fontWeight="bold"
            lineHeight="18px"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {category_icon ? `${category_icon} ` : ""}
            {category_name}
          </Text>
        </Flex>
      ) : (
        <Box height="18px" />
      )}

      <Text
        width="100%"
        fontSize="0.68rem"
        fontWeight="extrabold"
        textAlign="center"
        lineHeight="18px"
        height="18px"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        ⭐ {displayOfferTitle}
      </Text>

      <Flex align="center" justify="center" gap={3} height="52px">
        {businessLogo ? (
          <Image
            src={businessLogo}
            alt={business_name || "Business logo"}
            height="35px"
            width="35px"
            objectFit="cover"
            rounded="full"
          />
        ) : (
          <Flex
            height="35px"
            width="35px"
            rounded="full"
            bg="red.500"
            color="white"
            fontWeight="bold"
            align="center"
            justify="center"
            fontSize="0.9rem"
          >
            {business_name?.charAt(0).toUpperCase() || "B"}
          </Flex>
        )}

        {qr_code_url && redeemUrl && (
          <a
            href={redeemUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <Image
              src={qr_code_url}
              alt={`Redeem ${displayOfferTitle}`}
              height="48px"
              width="48px"
              border="2px solid #e11d48"
              rounded="md"
              bg="white"
              p={1}
            />
          </a>
        )}
      </Flex>

      <Text
        height="14px"
        lineHeight="14px"
        fontSize="0.7rem"
        color="red.600"
        fontWeight="extrabold"
        textAlign="center"
        whiteSpace="nowrap"
      >
        SCAN TO REDEEM
      </Text>

      <Text
        height="10px"
        lineHeight="10px"
        fontSize="0.5rem"
        color="gray.500"
        textAlign="center"
        whiteSpace="nowrap"
      >
        Valid in your postcode only
      </Text>
    </Box>
  );
}
